import React, { FC } from 'react';
import plLocale from 'date-fns/locale/pl';
import { Field, FieldProps } from 'formik';
import { get } from 'lodash';
import { DatePicker as MuiXDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePickerProps } from './DatePicker.types';
import { useStyles } from './DatePicker.styles';
import { useTranslator } from 'components/shared/hooks';
import { format, isValid, parse } from 'date-fns';
import { nullableEmptyString } from 'utils/string/provideStrings';
import classNames from 'classnames';

export const DatePickerFormat = 'dd.MM.yyyy';

const parseFormValue = (value: string | null | undefined): Date | null => {
  const normalized = nullableEmptyString(value);

  if (!normalized) {
    return null;
  }

  const isoDate = parse(normalized, 'yyyy-MM-dd', new Date());

  if (isValid(isoDate)) {
    return isoDate;
  }

  const displayDate = parse(normalized, DatePickerFormat, new Date());

  return isValid(displayDate) ? displayDate : null;
};

export const DatePicker: FC<DatePickerProps> = ({
  name,
  labelText,
  required,
  onChange,
  outputFormat = '',
  className,
  clearable = true,
  disableKeyboard = false,
}) => {
  const classes = useStyles();
  const trans = useTranslator();

  return (
    <Field name={name}>
      {({ field, form }: FieldProps) => {
        const isError = !!(get(form.errors, field.name) && get(form.touched, field.name));
        const value = parseFormValue(field.value);
        const errorMessage = get(form.errors, field.name) as string;

        const handleChange = (date: Date | null) => {
          if (date && isValid(date)) {
            const formattedOutput = outputFormat ? format(date, outputFormat) : format(date, 'yyyy-MM-dd');
            form.setFieldValue(field.name, format(date, 'yyyy-MM-dd'));
            onChange && onChange(formattedOutput, true);
            return;
          }

          form.setFieldValue(field.name, '');
          onChange && onChange('', false);
        };

        return (
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={plLocale}>
            <MuiXDatePicker
              label={labelText}
              value={value}
              onChange={handleChange}
              format={DatePickerFormat}
              className={classNames({ hasValidationError: isError }, classes.root, className)}
              slotProps={{
                textField: {
                  variant: 'standard',
                  error: isError,
                  helperText: isError ? errorMessage : undefined,
                  required,
                  name: field.name,
                  inputProps: {
                    'data-testid': `date-picker-${field.name}`,
                    readOnly: disableKeyboard,
                  },
                  InputLabelProps: {
                    classes: {
                      error: classes.labelError,
                    },
                  },
                  InputProps: {
                    classes: {
                      root: classes.formControl,
                    },
                  },
                  FormHelperTextProps: {
                    error: true,
                  },
                },
                field: {
                  clearable,
                },
                actionBar: {
                  actions: clearable ? ['clear', 'cancel', 'accept'] : ['cancel', 'accept'],
                },
              }}
              localeText={{
                cancelButtonLabel: trans('COMMON.FORM.DATE_PICKER.CANCEL'),
                okButtonLabel: trans('COMMON.FORM.DATE_PICKER.OK'),
                clearButtonLabel: trans('COMMON.FORM.DATE_PICKER.CLEAR'),
              }}
            />
          </LocalizationProvider>
        );
      }}
    </Field>
  );
};
